import { Button } from '@ctw/shared/components/button';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { formatDateToLocalHumanDateString, numberOfDaysBetween } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faArrowTrendUp, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays, endOfMonth } from 'date-fns';
import pluralize from 'pluralize';
import { useCallback, useMemo } from 'react';
import {
  getUiFlagLimitApproachingBannerHidden,
  getUiFlagLimitReachedBannerHidden,
  usePatientSummarizationUpgradeModal,
} from './patient-summarization-status-card';

export interface SummarizationLimitBannerProps {
  variant?: 'inline' | 'standalone';
  dismissable?: boolean;
  summaryJobId?: string;
  summaryJobStatus?: string;
}

export const PatientSummarizationLimitApproachingBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
}: SummarizationLimitBannerProps) => {
  const { getUiFlag, setUiFlag } = useZuiContext();
  const { summariesAllotted, summariesUsed } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitApproachingBannerHidden(), []);
  const hidden = dismissable && getUiFlag(flagKey, false);

  const hideBanner = useCallback(() => {
    setUiFlag(flagKey, true);
  }, [setUiFlag, flagKey]);

  const numberOfDaysUntilReset = numberOfDaysBetween({
    start: new Date(),
    end: addDays(endOfMonth(new Date()), 1),
  });

  return (
    <div
      className={twx('grid transition-[grid-template-rows,opacity] duration-300 ease-out', {
        'grid-rows-[0fr] opacity-0': hidden,
        'grid-rows-[1fr] opacity-100': !hidden,
      })}
    >
      <div className={tw`min-h-0 overflow-hidden`}>
        <div
          className={twx(
            'flex items-start justify-between gap-4 bg-caution-background p-4 text-caution-text',
            {
              'border-caution-main/15 border-t-2': variant === 'inline',
              'my-5 rounded-lg border border-caution-main/15': variant === 'standalone',
            },
          )}
        >
          <div className={tw`flex items-start gap-2`}>
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-4 w-4 pt-1`} />
            <div>
              {variant === 'standalone' && (
                <div className={tw`font-bold`}>
                  You're approaching your monthly AI summary limit
                </div>
              )}
              <div className={twx({ 'mt-0.5': variant === 'standalone' })}>
                {variant === 'inline' && <b>You're approaching your monthly AI summary limit.</b>}{' '}
                {summariesUsed.toLocaleString('en-US')} of{' '}
                {summariesAllotted.toLocaleString('en-US')} free summaries have been used this
                month. Resets in {pluralize('day', numberOfDaysUntilReset, true)}.
              </div>
            </div>
          </div>
          <div className={tw`flex items-center gap-2`}>
            {variant === 'standalone' ? (
              <Button
                type="button"
                variant="primary"
                onClick={openUpgradeModal}
                className={tw`bg-caution-main text-white hover:bg-caution-main/90`}
                telemetryInteractionName="patient_summary_upgrade_modal_opened"
                telemetryObfuscatedIdentifier="Learn More"
                telemetryContext={{
                  summaryJobId,
                  summaryJobStatus,
                  upgradeModalTrigger: 'learn-more-link',
                }}
              >
                <div className={tw`flex items-center space-x-2`}>
                  <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
                  <div className={tw`whitespace-nowrap text-sm`}>Learn More</div>
                </div>
              </Button>
            ) : (
              <Button
                type="button"
                variant="link"
                onClick={openUpgradeModal}
                className={tw`whitespace-nowrap font-medium text-warning-text underline`}
                telemetryInteractionName="patient_summary_upgrade_modal_opened"
                telemetryObfuscatedIdentifier="Learn More"
                telemetryContext={{
                  summaryJobId,
                  summaryJobStatus,
                  upgradeModalTrigger: 'learn-more-link',
                }}
              >
                Learn More
              </Button>
            )}
            {dismissable && (
              <Button
                type="button"
                variant="circular"
                size="xs"
                className={tw`mt-0.5 flex items-center justify-center bg-transparent p-1 text-warning-text shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                onClick={hideBanner}
              >
                <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PatientSummarizationLimitReachedBanner = ({
  variant = 'inline',
  dismissable = true,
  summaryJobId,
  summaryJobStatus,
}: SummarizationLimitBannerProps) => {
  const { getUiFlag, setUiFlag } = useZuiContext();
  const { summariesAllotted } = useUserPatientSummarizationContext();
  const { openUpgradeModal } = usePatientSummarizationUpgradeModal();
  const flagKey = useMemo(() => getUiFlagLimitReachedBannerHidden(), []);
  const hidden = dismissable && getUiFlag(flagKey, false);

  const hideBanner = useCallback(() => {
    setUiFlag(flagKey, true);
  }, [setUiFlag, flagKey]);

  const formattedSummaryLimitResetDate = formatDateToLocalHumanDateString(
    addDays(endOfMonth(new Date()), 1),
  );

  return (
    <div
      className={twx('grid transition-[grid-template-rows,opacity] duration-300 ease-out', {
        'grid-rows-[0fr] opacity-0': hidden,
        'grid-rows-[1fr] opacity-100': !hidden,
      })}
    >
      <div className={tw`min-h-0 overflow-hidden`}>
        <div
          className={twx(
            'flex items-start justify-between gap-4 bg-error-background p-4 text-error-text',
            {
              'border-error-main/15 border-t-2': variant === 'inline',
              'my-5 rounded-lg border border-error-main/15': variant === 'standalone',
            },
          )}
        >
          <div className={tw`flex items-start gap-2`}>
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-4 w-4 pt-1`} />
            <div>
              {variant === 'standalone' && (
                <div className={tw`font-bold`}>You've reached your monthly AI summary limit</div>
              )}
              <div className={twx({ 'mt-0.5': variant === 'standalone' })}>
                You've used all {summariesAllotted.toLocaleString('en-US')} free summaries this
                month. Upgrade for unlimited access or wait until your quota resets (
                {formattedSummaryLimitResetDate}).
              </div>
            </div>
          </div>
          <div className={tw`flex items-center gap-2`}>
            {variant === 'standalone' && (
              <Button
                type="button"
                variant="primary-destructive"
                onClick={openUpgradeModal}
                telemetryInteractionName="patient_summary_upgrade_modal_opened"
                telemetryObfuscatedIdentifier="Upgrade Now"
                telemetryContext={{
                  summaryJobId,
                  summaryJobStatus,
                  upgradeModalTrigger: 'upgrade-now-button',
                }}
              >
                <div className={tw`flex items-center space-x-2`}>
                  <FontAwesomeIcon icon={faArrowTrendUp} className={tw`h-4 w-4`} />
                  <div className={tw`whitespace-nowrap text-sm`}>Upgrade Now</div>
                </div>
              </Button>
            )}
            {dismissable && (
              <Button
                type="button"
                variant="circular"
                size="xs"
                className={tw`flex items-center justify-center bg-transparent p-1 text-warning-text shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
                onClick={hideBanner}
              >
                <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
